import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Download, Check, Search, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import { STICKERS, CATEGORIES, filterStickers, type Sticker, type StickerCategory } from "@/lib/index";

// ========== Sticker Card ==========
function StickerCard({
  sticker
}: {
  sticker: Sticker;
}) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sticker.emoji);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
    }
  };
  const handleDownload = () => {
    // Create a canvas to draw the emoji and download it
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = sticker.bgColor;
    ctx.roundRect(0, 0, 200, 200, 32);
    ctx.fill();
    ctx.font = "120px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(sticker.emoji, 100, 105);
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sticker.name}.png`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };
  return <motion.div layout initial={{
    opacity: 0,
    scale: 0.85,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.8,
    y: 10
  }} transition={{
    type: "spring",
    stiffness: 380,
    damping: 28
  }} onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)} className="group relative rounded-3xl overflow-hidden cursor-pointer select-none" style={{
    background: sticker.bgColor,
    boxShadow: hovered ? "0 16px 40px -8px oklch(0.52 0.18 145 / 0.22), 0 4px 12px -4px oklch(0.52 0.18 145 / 0.12)" : "0 4px 20px -6px oklch(0.52 0.18 145 / 0.10)",
    transform: hovered ? "translateY(-4px)" : "translateY(0)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease"
  }}>
      {/* Emoji */}
      <div className="flex items-center justify-center pt-7 pb-3 text-7xl">
        <motion.span animate={hovered ? {
        rotate: [0, -8, 8, -4, 0],
        scale: [1, 1.15, 1.1, 1.15, 1.08]
      } : {}} transition={{
        duration: 0.5,
        ease: "easeInOut"
      }} className="drop-shadow-sm">
          {sticker.emoji}
        </motion.span>
      </div>

      {/* Info */}
      <div className="px-4 pb-5">
        <h3 className="font-bold text-sm text-center text-foreground/90 mb-0.5">{sticker.name}</h3>
        <p className="text-xs text-center text-foreground/50 leading-snug line-clamp-1">
          {sticker.description}
        </p>
      </div>

      {/* Action Overlay */}
      <AnimatePresence>
        {hovered && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.18
      }} className="absolute inset-x-0 bottom-0 flex items-center gap-2 px-4 pb-4 pt-6" style={{
        background: `linear-gradient(to top, ${sticker.bgColor}f0 0%, ${sticker.bgColor}00 100%)`
      }}>
            <button onClick={handleCopy} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-white/70 hover:bg-white/90 text-xs font-semibold text-foreground/80 hover:text-foreground transition-all duration-150 active:scale-95" style={{
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}>
              {copied ? <>
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-green-600">已复制</span>
                </> : <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>复制</span>
                </>}
            </button>
            <button onClick={handleDownload} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-primary/90 hover:bg-primary text-xs font-semibold text-primary-foreground transition-all duration-150 active:scale-95" style={{
          boxShadow: "0 2px 8px oklch(0.52 0.18 145 / 0.30)"
        }}>
              <Download className="w-3.5 h-3.5" />
              <span>下载</span>
            </button>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}

// ========== Main Page ==========
export default function Home() {
  const [activeCategory, setActiveCategory] = useState<StickerCategory>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const filtered = useMemo(() => {
    let list = filterStickers(STICKERS, activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(s => s.name.includes(q) || s.description.includes(q) || s.tags.some(t => t.includes(q)) || s.emoji.includes(q));
    }
    return list;
  }, [activeCategory, searchQuery]);
  return <Layout>
      {/* ======== Hero ======== */}
      <section className="relative overflow-hidden pt-16 pb-12 sm:pt-20 sm:pb-16">
        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20" style={{
          background: "radial-gradient(circle, oklch(0.52 0.18 145) 0%, transparent 70%)"
        }} />
          <div className="absolute -top-16 -right-24 w-80 h-80 rounded-full opacity-15" style={{
          background: "radial-gradient(circle, oklch(0.70 0.16 155) 0%, transparent 70%)"
        }} />
          <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full opacity-10" style={{
          background: "radial-gradient(circle, oklch(0.88 0.14 125) 0%, transparent 70%)"
        }} />
          {/* Floating paws */}
          {["🐾", "🐾", "🐾", "🐾", "🐾"].map((paw, i) => <motion.span key={i} className="absolute text-2xl opacity-25" style={{
          left: `${10 + i * 20}%`,
          top: `${20 + i % 3 * 25}%`
        }} animate={{
          y: [0, -10, 0],
          rotate: [0, 10, -10, 0]
        }} transition={{
          duration: 3 + i * 0.7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: i * 0.4
        }}>
              {paw}
            </motion.span>)}
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/80 text-accent-foreground text-sm font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            <span>32 款可爱小狗贴图，完全免阿萨德费！</span>
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-4xl sm:text-6xl font-bold text-foreground mb-4 leading-tight">
            汪汪
            <span className="text-primary"> 贴图123屋 </span>
            🐶
          </motion.h1>

          {/* Subtitle */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            超可爱的小狗 emoji 贴图合集，开心、搞怪、困困、爱心……
            <br className="hidden sm:block" />
            一键复制或下载，让聊天更生动有趣！
          </motion.p>

          {/* Floating Emoji Showcase */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap mb-8">
            {["🐶", "🥰", "😜", "😎", "😴", "🍖", "🥳", "💪"].map((em, i) => <motion.div key={em} animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.2
          }} className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center text-3xl sm:text-4xl rounded-2xl bg-card shadow-sm border border-border/50" style={{
            boxShadow: "0 4px 16px -4px oklch(0.52 0.18 145 / 0.12)"
          }}>
                {em}
              </motion.div>)}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => document.getElementById("gallery")?.scrollIntoView({
            behavior: "smooth"
          })} className="px-7 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold text-base hover:opacity-90 active:scale-95 transition-all duration-200" style={{
            boxShadow: "0 6px 20px oklch(0.52 0.18 145 / 0.35)"
          }}>
              🎁 浏览全部贴图
            </button>
            <button onClick={() => document.getElementById("categories")?.scrollIntoView({
            behavior: "smooth"
          })} className="px-7 py-3 rounded-2xl bg-card border border-border font-semibold text-base text-foreground hover:bg-muted/50 active:scale-95 transition-all duration-200">
              🐾 按分类浏览
            </button>
          </motion.div>
        </div>
      </section>

      {/* ======== Stats Bar ======== */}
      <section className="border-y border-border/50 bg-card/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-3 divide-x divide-border/50">
          {[{
          value: "32",
          label: "款贴图",
          emoji: "🎨"
        }, {
          value: "8",
          label: "种分类",
          emoji: "🗂️"
        }, {
          value: "100%",
          label: "完全免费",
          emoji: "🎁"
        }].map(stat => <div key={stat.label} className="flex flex-col items-center py-2 gap-1">
              <div className="text-2xl">{stat.emoji}</div>
              <div className="text-2xl sm:text-3xl font-bold text-primary">{stat.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">{stat.label}</div>
            </div>)}
        </div>
      </section>

      {/* ======== Category Filter ======== */}
      <section id="categories" className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">按心情选贴图</h2>
            <p className="text-muted-foreground">选择你现在的心情，找到最合适的贴图</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {CATEGORIES.map(cat => <motion.button key={cat.id} whileTap={{
            scale: 0.93
          }} onClick={() => setActiveCategory(cat.id)} className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all duration-200 ${activeCategory === cat.id ? "bg-primary text-primary-foreground border-primary shadow-md" : "bg-card text-foreground/70 border-border/60 hover:border-primary/40 hover:bg-muted/40"}`} style={activeCategory === cat.id ? {
            boxShadow: "0 4px 14px oklch(0.52 0.18 145 / 0.30)"
          } : {}}>
                <span className="text-base">{cat.emoji}</span>
                <span>{cat.label}</span>
                {activeCategory === cat.id && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-foreground/20 text-xs font-bold">
                    {filterStickers(STICKERS, cat.id).length}
                  </span>}
              </motion.button>)}
          </div>
        </div>
      </section>

      {/* ======== Gallery ======== */}
      <section id="gallery" className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                贴图画廊
                <span className="ml-2 text-lg font-medium text-muted-foreground">
                  ({filtered.length} 款)
                </span>
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                悬停贴图可复制或下载 ✨
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input type="text" placeholder="搜索贴图..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-card border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all" />
            </div>
          </div>

          {/* Grid */}
          {filtered.length > 0 ? <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              <AnimatePresence mode="popLayout">
                {filtered.map(sticker => <StickerCard key={sticker.id} sticker={sticker} />)}
              </AnimatePresence>
            </motion.div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex flex-col items-center justify-center py-24 gap-4 text-center">
              <div className="text-6xl">🔍</div>
              <h3 className="text-xl font-semibold text-foreground">没找到对应贴图</h3>
              <p className="text-muted-foreground text-sm">试试其他关键词，或者切换分类~</p>
              <button onClick={() => {
            setSearchQuery("");
            setActiveCategory("all");
          }} className="mt-2 px-5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold active:scale-95 transition-transform">
                查看全部贴图
              </button>
            </motion.div>}
        </div>
      </section>

      {/* ======== Usage Guide ======== */}
      <section className="py-14 sm:py-20 bg-muted/30 border-t border-border/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">怎么使用贴图？</h2>
            <p className="text-muted-foreground">简单三步，让你的聊天充满活力！</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[{
            step: "01",
            emoji: "🖱️",
            title: "悬停贴图",
            desc: "将鼠标移到你喜欢的贴图上",
            color: "#FCE4EC"
          }, {
            step: "02",
            emoji: "📋",
            title: "复制或下载",
            desc: "点击「复制」可直接粘贴到聊天，点击「下载」保存为图片",
            color: "#E8F5E9"
          }, {
            step: "03",
            emoji: "💬",
            title: "发送给好友",
            desc: "把可爱的小狗贴图发给你的朋友，传递快乐！",
            color: "#E3F2FD"
          }].map((item, i) => <motion.div key={item.step} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.15,
            type: "spring",
            stiffness: 300,
            damping: 30
          }} className="relative rounded-3xl p-7 text-center" style={{
            background: item.color,
            boxShadow: "0 4px 24px -8px rgba(0,0,0,0.08)"
          }}>
                <div className="absolute -top-3 left-6 text-xs font-bold bg-primary text-primary-foreground px-2.5 py-0.5 rounded-full">
                  {item.step}
                </div>
                <div className="text-5xl mb-4">{item.emoji}</div>
                <h3 className="font-bold text-base text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-foreground/60 leading-relaxed">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ======== About ======== */}
      <section id="about" className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-5xl mb-5">🐶</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">关于123123汪汪贴图屋</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            汪汪贴图屋是一个专门为狗狗爱好者打造的免费贴图站点。我们精心收集了 32 款风格各异的
            可爱小狗贴图，从开心到搞怪，从爱心到困觉，总有一款能表达你此刻的心情。
            所有贴图均可免费复制和下载，快去和朋友分享快乐吧！
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-accent/70 text-accent-foreground font-semibold">
            <span>🐾</span>
            <span>让每一次聊天都充满汪气！</span>
            <span>🐾</span>
          </div>
        </div>
      </section>
    </Layout>;
}